import { Badge, Button, Icons } from '@commenda/kakashi';
import {
  ChatRoomAgentMessageKind,
  IChatRoomMessage,
} from '@commenda/types/src/chat-room/types';
import { cn, readableTimestamp } from '@commenda/utils';
import { ExternalMessageSystem } from '@prisma/client';
import { useState } from 'react';
import type { Components } from 'react-markdown';
import { MarkdownRenderer } from '../../../components/MarkdownRenderer';
import { AgentReplyActions, ChatProps } from '../../ChatRoom/types';
import ChatAvatar from '../ChatAvatar';
import CommendaAvatar from '../ChatAvatar/CommendaAvatar';
import { CommendaAgentMentionHelper } from '../commenda-agent-mention-helper';
import { SenderFirmBadge } from '../SenderFirmBadge';
import AgentReplyControls from './AgentReplyControls';
import ChatFileCard from './ChatFileCard';
import { ChatMessageActionBar } from './ChatMessageAction';
import ChatMessageLink from './ChatMessageLink';

const CHAT_MESSAGE_MARKDOWN_COMPONENT_OVERRIDES: Components = {
  a: ChatMessageLink,
};

/* ─── Sub-components ──────────────────────────────────────────────── */

/** Checkmark icon for read receipts — currently hidden, will be enabled when read status API is available */
// const ReadReceipt = () => (
//   <Icons.Check className="size-3 text-fg-success shrink-0" />
// );

/** Timestamp label */
const Timestamp = ({ date }: { date: string }) => (
  <span className="font-mulish text-xs font-normal leading-[15px] text-text-tertiary whitespace-nowrap">
    {readableTimestamp(date)}
  </span>
);

/** Sender name */
const SenderName = ({
  name,
  dimmed = false,
}: {
  name: string;
  dimmed?: boolean;
}) => (
  <span
    className={cn(
      'font-mulish text-sm leading-5 truncate',
      dimmed
        ? 'font-normal text-text-tertiary'
        : 'font-medium text-text-primary'
    )}
  >
    {name}
  </span>
);

/** Sending indicator */
const SendingIndicator = () => (
  <div className="flex items-center gap-xs shrink-0">
    <Icons.Loading01 className="size-3 text-text-tertiary animate-spin" />
    <span className="font-mulish text-xs font-normal leading-[15px] text-text-tertiary whitespace-nowrap">
      Sending...
    </span>
  </div>
);

const SentViaFooter = ({ clientName }: { clientName: string }) => (
  <div className="flex items-center gap-xs">
    <Icons.Stars01 className="size-3 text-text-tertiary shrink-0" />
    <span className="font-mulish text-xs font-normal leading-4 text-text-tertiary">
      Sent with{' '}
      <span className="font-medium text-text-secondary">{clientName}</span>
    </span>
  </div>
);

const ExternalMemberFooter = ({ email }: { email: string }) => (
  <div className="flex items-center gap-xs">
    <Icons.Mail01 className="size-3 text-text-tertiary shrink-0" />
    <span className="font-mulish text-xs font-normal leading-4 text-text-tertiary">
      Sent by an external member{' '}
      <span className="font-medium text-text-secondary">{email}</span>
    </span>
  </div>
);

/* ─── Header Row ──────────────────────────────────────────────────── */

const MessageHeader = ({
  senderName,
  firmName,
  sentAt,
  isCurrentUser,
  isHovered,
  isSending,
  isFromAgent,
}: {
  senderName: string;
  firmName?: string | null;
  sentAt: string;
  isCurrentUser: boolean;
  isHovered: boolean;
  isSending: boolean;
  isFromAgent: boolean;
}) => {
  if (isCurrentUser) {
    // Sender: right-aligned — status/time first, then name
    return (
      <div className="flex gap-md items-center justify-end w-full">
        <div className="flex gap-xs items-center shrink-0">
          {isSending ? <SendingIndicator /> : <Timestamp date={sentAt} />}
        </div>
        <SenderName name={senderName} />
        <SenderFirmBadge firmName={firmName} />
      </div>
    );
  }

  // Receiver: left-aligned — name, then time
  return (
    <div className="flex gap-md items-center w-full">
      <SenderName name={senderName} dimmed={isHovered} />
      <SenderFirmBadge firmName={firmName} />
      {isFromAgent && (
        <Badge variant="blue" size="sm">
          AI
        </Badge>
      )}
      <Timestamp date={sentAt} />
    </div>
  );
};

/* ─── Inline Reply Quote ──────────────────────────────────────────── */

const InlineReplyQuote = ({
  replyMessage,
}: {
  replyMessage: IChatRoomMessage;
}) => {
  const displayText = replyMessage.message
    ? replyMessage.message
    : (replyMessage.files?.[0]?.fileName ?? '');

  return (
    <div className="flex items-center gap-md px-lg py-md bg-bg-primary border-l-2 border-border-primary-hover w-full max-w-full min-w-0">
      <ChatAvatar
        size="sm"
        name={
          replyMessage.user?.name ||
          replyMessage.externalUser?.name ||
          replyMessage.externalUser?.email ||
          'Unknown'
        }
        image={replyMessage.user?.avatar}
        colorSeed={replyMessage.user?.id}
      />
      <span className="block flex-1 min-w-0 max-w-full font-mulish text-xs font-medium leading-4 text-text-primary truncate whitespace-nowrap">
        {displayText}
      </span>
    </div>
  );
};

/* ─── Email Message Body (stripped + expand) ─────────────────────── */

const EmailMessageBody = ({
  message,
  searchQuery,
}: {
  message: IChatRoomMessage;
  searchQuery?: string | null;
}) => {
  const [expanded, setExpanded] = useState(false);
  const displayText = expanded
    ? (message.rawMessage ?? message.message)
    : message.message;
  const rawLen = message.rawMessage?.trim().length ?? 0;
  const msgLen = message.message?.trim().length ?? 0;
  const hasMore = rawLen > msgLen + 20;

  return (
    <div className="w-full">
      <div className="font-mulish text-sm font-normal leading-5 text-text-primary break-words w-full [&_p:first-child]:mt-0 [&_p:last-child]:mb-0">
        <MarkdownRenderer
          content={CommendaAgentMentionHelper.linkMarkdownMentions(
            displayText ?? ''
          )}
          searchQuery={searchQuery}
          componentOverrides={CHAT_MESSAGE_MARKDOWN_COMPONENT_OVERRIDES}
          urlTransform={CommendaAgentMentionHelper.transformMarkdownUrl}
        />
      </div>
      {hasMore && (
        <Button
          variant="link"
          size="sm"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-1.5"
        >
          {expanded ? 'Show less' : 'Show full message'}
        </Button>
      )}
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────── */

const ChatMessageText = ({
  isIssueReported,
  replyMessage,
  message,
  onFileDownload,
  onSelectAsReply,
  onToggleMessagePin,
  removeTooltips,
  userId,
  searchQuery,
  isCurrentSearchResult,
  showAvatarAndName = true,
  corporationId,
  agentReplyActions,
}: {
  isIssueReported: boolean;
  replyMessage?: IChatRoomMessage;
  message: IChatRoomMessage;
  onFileDownload: ChatProps['onFileDownload'];
  onSelectAsReply?: (message: IChatRoomMessage) => void;
  onToggleMessagePin?: ChatProps['onToggleMessagePin'];
  removeTooltips?: boolean;
  userId: string;
  searchQuery?: string | null;
  isCurrentSearchResult?: boolean;
  showAvatarAndName?: boolean;
  corporationId?: number;
  agentReplyActions?: AgentReplyActions;
}) => {
  const isCurrentUser = message?.user?.id === userId;
  const isPinned = message?.isPinned && message?.pinnedByUserId === userId;
  const [isHovered, setIsHovered] = useState(false);

  const isAgentReply =
    message?.agentMessageKind === ChatRoomAgentMessageKind.REPLY;
  const isFromAgent =
    isAgentReply ||
    message?.agentMessageKind === ChatRoomAgentMessageKind.INTRO;

  const senderName = isFromAgent
    ? CommendaAgentMentionHelper.NAME
    : message?.externalMessageSource === ExternalMessageSystem.PYLON
      ? message?.externalUser?.name || 'Commenda'
      : message?.user?.name ||
        message?.externalUser?.name ||
        message?.externalUser?.email ||
        'Unknown';

  const truncatedName =
    senderName.length > 24 ? `${senderName.substring(0, 24)}...` : senderName;

  const isExternal =
    message?.externalMessageSource === ExternalMessageSystem.PYLON;

  // A message with no Commenda sender and no integration source came in by email from someone
  // outside the workspace, so the address it came from is the only identity we can vouch for.
  const externalMemberEmail =
    !message?.user?.id && !message?.externalMessageSource && !isFromAgent
      ? message?.externalUser?.email
      : undefined;

  // Bubble background: sender = blue, receiver = beige, issue = error
  const bubbleBg = isIssueReported
    ? 'bg-bg-error'
    : isCurrentUser
      ? 'bg-utility-blue-50'
      : 'bg-bg-secondary';

  return (
    <div
      className={cn(
        'flex gap-md items-start relative w-full',
        showAvatarAndName ? 'pt-lg' : 'pt-xs'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsHovered(false);
        }
      }}
    >
      {/* Avatar — outside content column, at top level */}
      <div
        className={cn(
          'shrink-0',
          isCurrentUser ? 'order-last' : 'order-first',
          !showAvatarAndName && 'invisible'
        )}
      >
        {isExternal || isFromAgent ? (
          <CommendaAvatar />
        ) : (
          <ChatAvatar
            size="lg"
            name={senderName}
            image={message?.user?.avatar}
            colorSeed={message?.user?.id}
          />
        )}
      </div>

      {/* Content column */}
      <div className="flex-1 flex flex-col gap-sm items-start min-w-0 relative">
        {/* Header — name + time only, no avatar */}
        {showAvatarAndName && (
          <MessageHeader
            senderName={truncatedName}
            firmName={message?.user?.firmName}
            sentAt={message?.sentAt}
            isCurrentUser={isCurrentUser}
            isHovered={isHovered}
            isSending={false}
            isFromAgent={isFromAgent}
          />
        )}

        {/* Message bubble — relative container for action bar */}
        <div
          className={cn(
            'flex flex-col gap-md justify-center overflow-visible rounded-xl relative max-w-[85%] min-w-0',
            isCurrentUser ? 'self-end items-end' : 'self-start items-start'
          )}
        >
          {/* Floating action bar — positioned above the bubble */}
          {(onSelectAsReply || onToggleMessagePin) && isHovered && (
            <div
              className={cn(
                'absolute -top-4 z-10',
                isCurrentUser ? 'left-0' : 'left-0'
              )}
            >
              <ChatMessageActionBar
                onReply={() => onSelectAsReply?.(message)}
                onPin={() => onToggleMessagePin?.(message.id)}
                isPinned={isPinned}
              />
            </div>
          )}
          {/* Inner content card — only render if there's text, reply, or issue */}
          {(replyMessage || isIssueReported || message?.message) && (
            <div
              className={cn(
                'flex flex-col gap-md items-start p-lg rounded-md w-full max-w-full min-w-0',
                bubbleBg,
                isCurrentSearchResult &&
                  'ring-2 ring-yellow-400 ring-opacity-75'
              )}
            >
              {replyMessage && <InlineReplyQuote replyMessage={replyMessage} />}

              {isIssueReported && (
                <div className="flex items-center gap-md text-text-error">
                  <Icons.AlertCircle className="size-4" />
                  <span className="font-mulish text-sm font-medium leading-5">
                    Issue Reported
                  </span>
                </div>
              )}

              {message?.message && (
                <EmailMessageBody message={message} searchQuery={searchQuery} />
              )}
            </div>
          )}

          {/* File attachments — outside the colored card */}
          {message?.files?.length > 0 && (
            <div className="flex flex-col w-full">
              {message.files.map((file) => (
                <ChatFileCard
                  key={file.id}
                  fileName={file?.fileName}
                  onFileDownload={onFileDownload}
                  fileId={file?.id}
                  fileUrl={file?.fileUrl}
                  corporationId={corporationId}
                />
              ))}
            </div>
          )}

          {externalMemberEmail && (
            <ExternalMemberFooter email={externalMemberEmail} />
          )}

          {message?.sentViaClientName && (
            <SentViaFooter clientName={message.sentViaClientName} />
          )}

          {isAgentReply && message.agentRun && agentReplyActions && (
            <AgentReplyControls
              messageId={message.id}
              myRating={message.agentRun.myRating}
              canRate={message.agentRun.canRate}
              agentReplyActions={agentReplyActions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageText;
